import React, { useState } from 'react';
import { Platform, StyleSheet, Image, TouchableOpacity, View } from 'react-native';
import { Callout, Marker } from 'react-native-maps';
import { Marker as WebMarker, InfoWindow } from 'react-google-maps';
import BaseText from '../BaseText';
import { scale } from 'react-native-size-matters';
import { Color, CommonStyles } from '../../styles';
import { cardStyle, getStringRating } from '../resultCards/card.utils';
import Images from '../../constants/Images';
import RowView from '../RowView';
import Icon from '../Icon';
import { CommonActions } from '@react-navigation/native';
import { formatCurrency } from './../../utils/currency.utils';

export const CarMarkerContent = ({ carResponse, onPress, currency, currencyCulture }) => {
  const totalFare = formatCurrency(carResponse?.TotalFare, currency, currencyCulture);
  return (
    <RowView style={{ justifyContent: 'space-between' }}>
      <View style={{ alignItems: 'center' }}>
        <BaseText>{carResponse?.Class}</BaseText>
        <Image
          style={styles.image}
          source={{ uri: carResponse?.CarImageURL } || Images.defaultUser}
        />
        <Image
          style={styles.image}
          source={Images.car['50x20'][carResponse?.VendorCode] || Images.defaultUser}
        />
      </View>
      <View style={CommonStyles.px3}>
        <BaseText>{carResponse?.CarName}</BaseText>
        <BaseText>
          {carResponse?.PickupDistance} {carResponse?.PickupDistanceUnit}{' '}
          {carResponse?.PickupCityState}
        </BaseText>
        <BaseText>On {carResponse?.RequestType}</BaseText>
      </View>
      <View style={{ alignItems: 'center' }}>
        <BaseText style={styles.priceSection}>{totalFare} </BaseText>
        <TouchableOpacity onPress={onPress}>
          <BaseText style={styles.priceSection}>Per Day</BaseText>
          <BaseText style={styles.priceSection}>Select</BaseText>
        </TouchableOpacity>
      </View>
    </RowView>
  );
};

export const AirMarkerContent = ({ flightResponse }) => {
  return (
    <RowView style={{ justifyContent: 'space-between' }}>
      <View style={{ alignItems: 'center' }}>
        <Icon name="smartFare" size={24} color={Color.primary} />
        <BaseText>{flightResponse?.NoOfFlights} flights</BaseText>
      </View>
      <View style={CommonStyles.px3}>
        <BaseText>
          {flightResponse?.airportCityName} ({flightResponse?.airportCode})
        </BaseText>
        <BaseText>
          {flightResponse?.Minimumduration} - {flightResponse?.MaximumDuration}hrs duration{' '}
        </BaseText>
        <BaseText>{flightResponse?.NoOfStops} Stop(s)</BaseText>
      </View>
      <View style={{ alignItems: 'flex-end' }}>
        <BaseText>From</BaseText>
        <BaseText>${flightResponse?.MinimumTotalCost} </BaseText>
      </View>
    </RowView>
  );
};

export const HotelMarkerContent = ({ hotelResponse, onPress }) => {
  return (
    <>
      <TouchableOpacity onPress={onPress}>
        <BaseText>{hotelResponse?.HotelName}</BaseText>
        <Image
          style={[cardStyle.rating, CommonStyles.mr2]}
          source={
            Images.rating[
              hotelResponse.ClassLevel * 10 || getStringRating(hotelResponse.StarRating)
            ]
          }
        />
        <BaseText>{hotelResponse?.Address2}</BaseText>
        <BaseText>$ {parseInt(hotelResponse?.price)}</BaseText>
      </TouchableOpacity>
    </>
  );
};

const InfoMarker = ({ latitude, longitude, label, children, ...rest }) => {
  const [showInfo, setShowInfo] = useState(false);
  if (Platform.OS === 'web') {
    return (
      <WebMarker
        tracksViewChanges={false}
        onClick={() => setShowInfo(!showInfo)}
        icon={{
          scaledSize: {
            width: 36,
            height: 36,
          },
          url: Images.markerAndroid.hotel,
        }}
        position={{
          lat: latitude,
          lng: longitude,
        }}
        label={{
          text: label,
          color: '#fff',
          fontSize: '10px',
        }}
        {...rest}
      >
        {showInfo && <InfoWindow onCloseClick={() => setShowInfo(false)}>{children}</InfoWindow>}
      </WebMarker>
    );
  } else {
    if (Platform.OS === 'ios') {
      return (
        <Marker
          coordinate={{
            latitude: latitude,
            longitude: longitude,
          }}
          tracksViewChanges={false}
          centerOffset={{ x: 0, y: -10 }}
          {...rest}
        >
          <CustomMarker label={label} />
          <Callout style={[styles.plainView, { width: scale(270) }]}>{children}</Callout>
        </Marker>
      );
    } else {
      return (
        <Marker
          coordinate={{
            latitude: latitude,
            longitude: longitude,
          }}
          tracksViewChanges={false}
          {...rest}
        >
          <CustomMarker label={label} />
          <Callout style={[styles.plainView, { width: scale(270) }]}>{children}</Callout>
        </Marker>
      );
    }
  }
};
const iconSize = scale(32);
export const styles = StyleSheet.create({
  plainView: {
    width: scale(200),
  },
  priceSection: {
    color: Color.primary,
  },
  image: {
    height: scale(18),
    width: iconSize,
    resizeMode: 'contain',
    ...CommonActions.mr3,
  },
  text: {
    color: '#FFF',
    textAlign: 'center',
    width: 48,
    paddingTop: 22,
    paddingLeft: 18,
  },
  markerContainer: {
    alignItems: 'center',
  },
  bubble: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    backgroundColor: '#0278CB',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderColor: '#0278CB',
    borderWidth: 0.5,
  },
  amount: {
    color: '#fff',
    fontSize: 16,
  },
  arrowContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    width: 14,
    height: 7,
    backgroundColor: 'transparent',
  },
  arrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderTopWidth: 7,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#0278CB',
  },
});

const CustomMarker = ({ label }) => (
  <View style={styles.markerContainer}>
    <View style={styles.bubble}>
      <BaseText style={styles.amount}>{label}</BaseText>
    </View>
    <View style={styles.arrowContainer}>
      <View style={styles.arrow} />
    </View>
  </View>
);

export default React.memo(InfoMarker);
